/* =====================================================================
 * MessengerModal — Slack / Teams / Discord 채널 파일 선택 모달
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  Hash,
  Paperclip,
  RefreshCw,
  Loader,
} from 'lucide-react';

import { Modal } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { SkeletonLine } from '@auw/workspace-ui';
import { Tabs, type TabItem } from '@auw/workspace-ui';
import { RingSpinner } from '@auw/workspace-ui';
import {
  listPlugins,
  listSlackChannels,
  listSlackChannelFiles,
  listMicrosoftTeamsTeams,
  listMicrosoftTeamsChannels,
  listMicrosoftTeamsChannelFiles,
  listDiscordGuilds,
  listDiscordChannels,
  listDiscordChannelFiles,
  getMessengerAttachmentDownloadUrl,
  downloadMessengerAttachment,
  downloadMessengerAttachmentThumbnail,
} from '@auw/api-client';
import type {
  MessengerProvider,
  MessengerChannelItem,
  MessengerFileItem,
  PluginInfo,
  MicrosoftTeamsTeamItem,
  DiscordGuildItem,
} from '@auw/api-client';
import type { FileAttachment } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { queryKeys } from '@auw/workspace-utils';
import {
  CHAT_ATTACHMENT_MAX_COUNT,
  MODAL_GC_TIME,
  MODAL_STALE_TIME,
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  getRemainingAttachmentSlots,
  isAttachmentSizeAllowed,
} from './attachmentConstraints';

interface MessengerModalProps {
  open: boolean;
  onClose: () => void;
  initialTab: MessengerProvider;
  onSelect: (files: FileAttachment[]) => void;
  existingAttachmentCount?: number;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
}

const CHANNEL_PAGE_SIZE = 50;
const FILE_PAGE_SIZE = 50;
const IMAGE_EXT_PATTERN = /\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/i;
const IMAGE_PREVIEW_MAX_BYTES = 3 * 1024 * 1024;
const IMAGE_PREVIEW_CACHE_LIMIT = 80;
const IMAGE_PREVIEW_CONCURRENCY = 3;
const IMAGE_PREVIEW_TOO_LARGE = 'image_preview_too_large';

const imagePreviewCache = new Map<string, string>();
const imagePreviewInflight = new Map<string, Promise<string>>();
let imagePreviewActiveCount = 0;
const imagePreviewQueue: Array<() => void> = [];

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function providerLabel(provider: MessengerProvider): string {
  if (provider === 'slack') return 'Slack';
  if (provider === 'microsoft_teams') return 'Microsoft Teams';
  return 'Discord';
}

function providerLogoSrc(provider: MessengerProvider): string {
  if (provider === 'slack') return '/logo/slack.png';
  if (provider === 'microsoft_teams') return '/logo/teams.webp';
  return '/logo/discord.png';
}

function messengerFileKey(item: Pick<MessengerFileItem, 'provider' | 'id'>): string {
  return `${item.provider}:${item.id}`;
}

function isImageMessengerFile(item: Pick<MessengerFileItem, 'name' | 'mimeType'>): boolean {
  const mime = String(item.mimeType || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  return IMAGE_EXT_PATTERN.test(item.name || '');
}

function isTooLargePreviewError(err: unknown): boolean {
  return err instanceof Error && err.message === IMAGE_PREVIEW_TOO_LARGE;
}

function extractHttpStatus(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const match = err.message.match(/\((\d{3})\)/);
  if (!match) return null;
  const status = Number(match[1]);
  return Number.isFinite(status) ? status : null;
}

async function withImagePreviewConcurrency<T>(task: () => Promise<T>): Promise<T> {
  if (imagePreviewActiveCount >= IMAGE_PREVIEW_CONCURRENCY) {
    await new Promise<void>((resolve) => imagePreviewQueue.push(resolve));
  }
  imagePreviewActiveCount += 1;
  try {
    return await task();
  } finally {
    imagePreviewActiveCount = Math.max(0, imagePreviewActiveCount - 1);
    const next = imagePreviewQueue.shift();
    if (next) next();
  }
}

function touchImagePreviewCache(key: string): string | null {
  const cached = imagePreviewCache.get(key);
  if (!cached) return null;
  imagePreviewCache.delete(key);
  imagePreviewCache.set(key, cached);
  return cached;
}

function pruneImagePreviewCache(): void {
  while (imagePreviewCache.size > IMAGE_PREVIEW_CACHE_LIMIT) {
    const oldestKey = imagePreviewCache.keys().next().value as string | undefined;
    if (!oldestKey) return;
    const url = imagePreviewCache.get(oldestKey);
    imagePreviewCache.delete(oldestKey);
    if (url) URL.revokeObjectURL(url);
  }
}

async function getMessengerImagePreview(file: MessengerFileItem): Promise<string> {
  const key = messengerFileKey(file);
  const cached = touchImagePreviewCache(key);
  if (cached) return cached;

  if (typeof file.size === 'number' && file.size > IMAGE_PREVIEW_MAX_BYTES) {
    throw new Error(IMAGE_PREVIEW_TOO_LARGE);
  }

  const inflight = imagePreviewInflight.get(key);
  if (inflight) return inflight;

  const task = withImagePreviewConcurrency(async () => {
    let imageFile: File;
    try {
      imageFile = await downloadMessengerAttachmentThumbnail(file.provider, file.id, file.name);
    } catch (thumbErr) {
      // 썸네일이 4xx/429면 원본 fallback을 생략해 불필요한 대용량 다운로드를 막습니다.
      const status = extractHttpStatus(thumbErr);
      const shouldFallbackToOriginal = status === null || status >= 500;
      if (!shouldFallbackToOriginal) {
        throw thumbErr;
      }
      imageFile = await downloadMessengerAttachment(file.provider, file.id, file.name);
    }
    if (imageFile.size > IMAGE_PREVIEW_MAX_BYTES) {
      throw new Error(IMAGE_PREVIEW_TOO_LARGE);
    }
    const objectUrl = URL.createObjectURL(imageFile);
    imagePreviewCache.set(key, objectUrl);
    pruneImagePreviewCache();
    return objectUrl;
  });
  imagePreviewInflight.set(key, task);
  try {
    return await task;
  } finally {
    imagePreviewInflight.delete(key);
  }
}

const MessengerImageEmbed: React.FC<{ file: MessengerFileItem }> = ({ file }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [tooLarge, setTooLarge] = useState(false);
  const [canLoad, setCanLoad] = useState(false);
  const isImageFile = isImageMessengerFile(file);

  useEffect(() => {
    if (!isImageFile) {
      setPreviewUrl(null);
      setFailed(false);
      setTooLarge(false);
      setCanLoad(false);
      return;
    }

    setPreviewUrl(null);
    setFailed(false);
    setTooLarge(false);
    setCanLoad(false);
  }, [file.provider, file.id, file.name, file.mimeType, file.size, isImageFile]);

  useEffect(() => {
    if (!isImageFile) return;
    const node = hostRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCanLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '140px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [file.provider, file.id, isImageFile]);

  useEffect(() => {
    if (!isImageFile) return;
    if (!canLoad) return;

    let alive = true;
    const key = messengerFileKey(file);
    const cached = touchImagePreviewCache(key);
    if (cached) {
      setPreviewUrl(cached);
      setFailed(false);
      setTooLarge(false);
      return;
    }

    setFailed(false);
    setTooLarge(false);
    setPreviewUrl(null);

    const run = async () => {
      try {
        const objectUrl = await getMessengerImagePreview(file);
        if (!alive) return;
        setPreviewUrl(objectUrl);
      } catch (err) {
        if (!alive) return;
        if (isTooLargePreviewError(err)) {
          setTooLarge(true);
          return;
        }
        setFailed(true);
      }
    };
    void run();

    return () => {
      alive = false;
    };
  }, [file.provider, file.id, file.name, file.size, canLoad, isImageFile]);

  if (!isImageFile) return null;

  return (
    <ImageEmbedWrap ref={hostRef}>
      {previewUrl ? (
        <ImageEmbed src={previewUrl} alt={file.name} loading="lazy" />
      ) : tooLarge ? (
        <ImageEmbedFallback>BIG</ImageEmbedFallback>
      ) : failed ? (
        <ImageEmbedFallback>IMG</ImageEmbedFallback>
      ) : (
        <ImageEmbedSkeleton />
      )}
    </ImageEmbedWrap>
  );
};

export const MessengerModal: React.FC<MessengerModalProps> = ({
  open,
  onClose,
  initialTab,
  onSelect,
  existingAttachmentCount = 0,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
}) => {
  const { t } = useTranslation('common');
  const { openPluginDetail } = useWorkspaceNavigate();
  const addToast = useChatStore((s) => s.addToast);

  const [activeTab, setActiveTab] = useState<MessengerProvider>(initialTab);
  const [scopeQuery, setScopeQuery] = useState('');
  const [channelQuery, setChannelQuery] = useState('');
  const [fileQuery, setFileQuery] = useState('');
  const [debouncedScopeQuery, setDebouncedScopeQuery] = useState('');
  const [debouncedChannelQuery, setDebouncedChannelQuery] = useState('');
  const [debouncedFileQuery, setDebouncedFileQuery] = useState('');

  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedGuildId, setSelectedGuildId] = useState('');

  const [selectedChannel, setSelectedChannel] = useState<MessengerChannelItem | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Map<string, MessengerFileItem>>(new Map());

  const {
    data: pluginsData,
    isFetching: pluginsFetching,
    refetch: refetchPlugins,
  } = useQuery<PluginInfo[]>({
    queryKey: queryKeys.plugins.list(),
    queryFn: async () => {
      const resp = await listPlugins();
      return resp.plugins;
    },
    enabled: open,
    refetchOnWindowFocus: true,
  });
  const plugins = pluginsData ?? [];
  const pluginsLoading = pluginsFetching && !pluginsData;

  const tabItems: TabItem[] = [
    { id: 'slack', label: 'Slack', icon: <TabIcon src="/logo/slack.png" alt="Slack" /> },
    { id: 'microsoft_teams', label: 'Microsoft Teams', icon: <TabIcon src="/logo/teams.webp" alt="Microsoft Teams" /> },
    { id: 'discord', label: 'Discord', icon: <TabIcon src="/logo/discord.png" alt="Discord" /> },
  ];

  const isConnected = useCallback((provider: MessengerProvider): boolean => {
    return plugins.some((p) => p.id === provider && p.connected);
  }, [plugins]);
  const activeConnected = isConnected(activeTab);

  const teamsQuery = useQuery<MicrosoftTeamsTeamItem[]>({
    queryKey: ['messenger-teams', debouncedScopeQuery],
    enabled: open && activeTab === 'microsoft_teams' && !pluginsLoading && isConnected('microsoft_teams'),
    staleTime: MODAL_STALE_TIME,
    queryFn: async () => {
      const resp = await listMicrosoftTeamsTeams(100, undefined, debouncedScopeQuery || undefined);
      return resp.teams ?? [];
    },
  });

  const guildsQuery = useQuery<DiscordGuildItem[]>({
    queryKey: ['messenger-guilds', debouncedScopeQuery],
    enabled: open && activeTab === 'discord' && !pluginsLoading && isConnected('discord'),
    staleTime: MODAL_STALE_TIME,
    queryFn: async () => {
      const resp = await listDiscordGuilds(100, undefined, debouncedScopeQuery || undefined);
      return resp.guilds ?? [];
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedScopeQuery(scopeQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [scopeQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedChannelQuery(channelQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [channelQuery]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFileQuery(fileQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [fileQuery]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab);
    setScopeQuery('');
    setChannelQuery('');
    setFileQuery('');
    setDebouncedScopeQuery('');
    setDebouncedChannelQuery('');
    setDebouncedFileQuery('');
    setSelectedTeamId('');
    setSelectedGuildId('');
    setSelectedChannel(null);
    setSelectedFiles(new Map());
    void refetchPlugins();
  }, [open, initialTab, refetchPlugins]);

  useEffect(() => {
    if (activeTab !== 'microsoft_teams') return;
    const teams = teamsQuery.data ?? [];
    if (teams.length === 0) {
      setSelectedTeamId('');
      return;
    }
    if (!selectedTeamId || !teams.some((team) => team.id === selectedTeamId)) {
      setSelectedTeamId(teams[0].id);
    }
  }, [activeTab, teamsQuery.data, selectedTeamId]);

  useEffect(() => {
    if (activeTab !== 'discord') return;
    const guilds = guildsQuery.data ?? [];
    if (guilds.length === 0) {
      setSelectedGuildId('');
      return;
    }
    if (!selectedGuildId || !guilds.some((guild) => guild.id === selectedGuildId)) {
      setSelectedGuildId(guilds[0].id);
    }
  }, [activeTab, guildsQuery.data, selectedGuildId]);

  useEffect(() => {
    setSelectedChannel(null);
    setSelectedFiles(new Map());
    setScopeQuery('');
    setChannelQuery('');
    setFileQuery('');
    setDebouncedScopeQuery('');
    setDebouncedChannelQuery('');
    setDebouncedFileQuery('');
  }, [activeTab]);
  const channelsQuery = useInfiniteQuery({
    queryKey: [
      'messenger-modal-channels',
      activeTab,
      selectedTeamId,
      selectedGuildId,
      debouncedChannelQuery,
    ],
    enabled: open
      && !pluginsLoading
      && activeConnected
      && (activeTab !== 'microsoft_teams' || !!selectedTeamId)
      && (activeTab !== 'discord' || !!selectedGuildId),
    initialPageParam: undefined as string | undefined,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    queryFn: async ({ pageParam }) => {
      if (activeTab === 'slack') {
        const resp = await listSlackChannels(
          CHANNEL_PAGE_SIZE,
          pageParam || undefined,
          debouncedChannelQuery || undefined,
        );
        const mapped: MessengerChannelItem[] = (resp.channels ?? []).map((ch) => ({
          id: ch.id,
          provider: 'slack',
          name: `# ${ch.name}`,
        }));
        return {
          channels: mapped,
          nextPageToken: resp.nextCursor ?? null,
        };
      }
      if (activeTab === 'microsoft_teams') {
        const resp = await listMicrosoftTeamsChannels(
          selectedTeamId,
          debouncedChannelQuery || undefined,
        );
        const mapped: MessengerChannelItem[] = (resp.channels ?? []).map((ch) => ({
          id: ch.id,
          provider: 'microsoft_teams',
          name: ch.name,
          teamId: ch.teamId,
          subtitle: ch.membershipType || undefined,
        }));
        return {
          channels: mapped,
          nextPageToken: null,
        };
      }
      const resp = await listDiscordChannels(
        selectedGuildId,
        debouncedChannelQuery || undefined,
      );
      const mapped: MessengerChannelItem[] = (resp.channels ?? []).map((ch) => ({
        id: ch.id,
        provider: 'discord',
        name: `# ${ch.name}`,
        guildId: ch.guildId,
      }));
      return {
        channels: mapped,
        nextPageToken: null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });

  const channels = useMemo(
    () => channelsQuery.data?.pages.flatMap((p) => p.channels) ?? [],
    [channelsQuery.data],
  );
  const channelsLoading = channelsQuery.isLoading;
  const channelsLoadingMore = channelsQuery.isFetchingNextPage;
  const channelsError = channelsQuery.error instanceof Error ? channelsQuery.error.message : null;
  const hasChannelsNextPage = !!channelsQuery.hasNextPage;

  useEffect(() => {
    if (channels.length === 0) {
      setSelectedChannel(null);
      return;
    }
    setSelectedChannel((prev) => {
      if (prev && channels.some((c) => c.provider === prev.provider && c.id === prev.id)) {
        return prev;
      }
      return channels[0];
    });
  }, [channels]);

  const filesQuery = useInfiniteQuery({
    queryKey: [
      'messenger-modal-files',
      selectedChannel?.provider ?? '',
      selectedChannel?.id ?? '',
      selectedChannel?.teamId ?? '',
      debouncedFileQuery,
    ],
    enabled: open && !!selectedChannel && activeConnected,
    initialPageParam: undefined as string | undefined,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    queryFn: async ({ pageParam }) => {
      const channel = selectedChannel!;
      if (channel.provider === 'slack') {
        const page = Number(pageParam || '1') || 1;
        const resp = await listSlackChannelFiles(
          channel.id,
          page,
          FILE_PAGE_SIZE,
          debouncedFileQuery || undefined,
        );
        const mapped: MessengerFileItem[] = (resp.files ?? []).map((f) => ({
          id: f.id,
          provider: 'slack',
          channelId: channel.id,
          name: f.name,
          mimeType: f.mimeType,
          size: f.size,
        }));
        return {
          files: mapped,
          nextPageToken: resp.nextPageToken ?? null,
        };
      }
      if (channel.provider === 'microsoft_teams') {
        if (!channel.teamId) {
          throw new Error(t('messenger.noTeamInfo'));
        }
        const resp = await listMicrosoftTeamsChannelFiles(
          channel.teamId,
          channel.id,
          FILE_PAGE_SIZE,
          pageParam || undefined,
          debouncedFileQuery || undefined,
        );
        const mapped: MessengerFileItem[] = (resp.files ?? []).map((f) => ({
          id: f.id,
          provider: 'microsoft_teams',
          channelId: channel.id,
          teamId: channel.teamId,
          name: f.name,
          mimeType: f.mimeType,
          size: f.size,
        }));
        return {
          files: mapped,
          nextPageToken: resp.nextPageToken ?? null,
        };
      }
      const resp = await listDiscordChannelFiles(
        channel.id,
        FILE_PAGE_SIZE,
        pageParam || undefined,
        debouncedFileQuery || undefined,
      );
      const mapped: MessengerFileItem[] = (resp.files ?? []).map((f) => ({
        id: f.id,
        provider: 'discord',
        channelId: channel.id,
        name: f.name,
        mimeType: f.mimeType,
        size: f.size,
      }));
      return {
        files: mapped,
        nextPageToken: resp.nextCursor ?? null,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });

  const files = useMemo(
    () => filesQuery.data?.pages.flatMap((p) => p.files) ?? [],
    [filesQuery.data],
  );
  const filesLoading = filesQuery.isLoading;
  const filesLoadingMore = filesQuery.isFetchingNextPage;
  const filesError = filesQuery.error instanceof Error ? filesQuery.error.message : null;
  const hasFilesNextPage = !!filesQuery.hasNextPage;

  useEffect(() => {
    setSelectedFiles(new Map());
  }, [selectedChannel?.provider, selectedChannel?.id]);

  const handleSelectChannel = useCallback((channel: MessengerChannelItem) => {
    setSelectedChannel(channel);
  }, []);

  const handleToggleFile = useCallback((file: MessengerFileItem) => {
    const key = messengerFileKey(file);
    const isSelected = selectedFiles.has(key);

    if (!isSelected && !isAttachmentSizeAllowed(file.size)) {
      const toast = buildAttachmentSizeLimitToast([{ name: file.name, size: file.size }]);
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
      return;
    }

    if (!isSelected && getRemainingAttachmentSlots(existingAttachmentCount, selectedFiles.size) === 0) {
      const toast = buildAttachmentCountLimitToast();
      addToast({
        type: 'warning',
        title: toast.title,
        description: toast.description,
      });
      return;
    }

    setSelectedFiles((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      next.set(key, file);
      return next;
    });
  }, [addToast, existingAttachmentCount, selectedFiles]);

  const handleAttach = useCallback(() => {
    if (selectedFiles.size === 0) return;
    const attachments: FileAttachment[] = Array.from(selectedFiles.values()).map((item) => ({
      id: uuid(),
      name: item.name,
      type: item.mimeType || 'application/octet-stream',
      size: item.size ?? 0,
      remoteUrl: getMessengerAttachmentDownloadUrl(item.provider, item.id),
      messengerSource: item.provider,
      messengerChannelId: item.channelId,
      messengerFileId: item.id,
    }));
    onSelect(attachments);
    onClose();
  }, [selectedFiles, onSelect, onClose]);

  const handleGoToPlugin = useCallback(() => {
    const pluginId = activeTab;
    openPluginDetail(pluginId);
    onClose();
  }, [activeTab, onClose, openPluginDetail]);

  const renderNotConnected = () => (
    <NotConnectedArea>
      <EmptyState
        icon={(
          <MessengerLogoWrap>
            <img
              src={providerLogoSrc(activeTab)}
              alt={providerLabel(activeTab)}
              width={48}
              height={48}
            />
          </MessengerLogoWrap>
        )}
        title={`${providerLabel(activeTab)}`}
        description={t('messenger.connectFromPlugins')}
        action={
          <Button variant="primary" size="md" onClick={handleGoToPlugin}>
            {t('messenger.connect')}
          </Button>
        }
      />
    </NotConnectedArea>
  );

  const selectedSummary = useMemo(() => {
    if (selectedFiles.size === 0) {
      return existingAttachmentCount > 0
        ? `${existingAttachmentCount}/${CHAT_ATTACHMENT_MAX_COUNT}`
        : t('messenger.selectFile');
    }
    const totalBytes = Array.from(selectedFiles.values()).reduce((acc, item) => acc + (item.size || 0), 0);
    return t('messenger.filesSelected', { count: selectedFiles.size, size: formatFileSize(totalBytes), total: existingAttachmentCount + selectedFiles.size, max: CHAT_ATTACHMENT_MAX_COUNT });
  }, [existingAttachmentCount, selectedFiles]);

  const teamOptions = teamsQuery.data ?? [];
  const guildOptions = guildsQuery.data ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('messenger.title')}
      titleIcon={<MessageCircle size={18} />}
      width="980px"
      resizable
      defaultHeight={680}
      minWidth={520}
      minHeight={380}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
    >
      <ModalBody>
        <Tabs
          items={tabItems}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as MessengerProvider)}
        />
        <TabContent>
          {pluginsLoading ? (
            <LoadingWrap>
              <RingSpinner size={24} />
            </LoadingWrap>
          ) : !isConnected(activeTab) ? (
            renderNotConnected()
          ) : (
            <BrowserArea>
              <Pane>
                <PaneHeader>
                  {activeTab !== 'slack' && (
                    <ScopeSelectWrap>
                      <Input
                        variant="search"
                        inputSize="sm"
                        placeholder={activeTab === 'microsoft_teams' ? t('messenger.searchTeam') : t('messenger.searchServer')}
                        value={scopeQuery}
                        onChange={(e) => setScopeQuery(e.target.value)}
                      />
                      {activeTab === 'microsoft_teams' ? (
                        <NativeSelect
                          value={selectedTeamId}
                          onChange={(e) => setSelectedTeamId(e.target.value)}
                        >
                          {teamOptions.length === 0 && <option value="">{t('messenger.noTeams')}</option>}
                          {teamOptions.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </NativeSelect>
                      ) : (
                        <NativeSelect
                          value={selectedGuildId}
                          onChange={(e) => setSelectedGuildId(e.target.value)}
                        >
                          {guildOptions.length === 0 && <option value="">{t('messenger.noServers')}</option>}
                          {guildOptions.map((guild) => (
                            <option key={guild.id} value={guild.id}>
                              {guild.name}
                            </option>
                          ))}
                        </NativeSelect>
                      )}
                    </ScopeSelectWrap>
                  )}
                  <Input
                    variant="search"
                    inputSize="sm"
                    placeholder={t('messenger.searchChannel')}
                    value={channelQuery}
                    onChange={(e) => setChannelQuery(e.target.value)}
                  />
                </PaneHeader>
                <ListTitle>{t('messenger.channels')}</ListTitle>
                <ListBox>
                  {channelsLoading && channels.length === 0 ? (
                    <SkeletonWrap>
                      {Array.from({ length: 6 }).map((_, i) => (
                        <SkeletonLine key={`channel-skeleton-${i}`} width={`${62 + Math.random() * 25}%`} height="16px" />
                      ))}
                    </SkeletonWrap>
                  ) : channelsError ? (
                    <ErrorText>{channelsError}</ErrorText>
                  ) : channels.length === 0 ? (
                    <EmptyHint>{t('messenger.noChannels')}</EmptyHint>
                  ) : (
                    <ItemsList>
                      {channels.map((channel) => (
                        <ChannelRow
                          key={`${channel.provider}:${channel.id}`}
                          $active={selectedChannel?.provider === channel.provider && selectedChannel?.id === channel.id}
                          onClick={() => handleSelectChannel(channel)}
                        >
                          <Hash size={14} />
                          <span>{channel.name}</span>
                        </ChannelRow>
                      ))}
                    </ItemsList>
                  )}
                  {channelsLoadingMore && channels.length > 0 && (
                    <LoadMoreIndicator>
                      <Loader size={14} className="spin" />
                      <span>{t('email.loading')}</span>
                    </LoadMoreIndicator>
                  )}
                  {!channelsLoading && hasChannelsNextPage && (
                    <LoadMoreBtn
                      onClick={() => { void channelsQuery.fetchNextPage(); }}
                    >
                      {t('messenger.loadMore')}
                    </LoadMoreBtn>
                  )}
                </ListBox>
              </Pane>

              <Pane>
                <PaneHeader>
                  <Input
                    variant="search"
                    inputSize="sm"
                    placeholder={t('messenger.searchFile')}
                    value={fileQuery}
                    onChange={(e) => setFileQuery(e.target.value)}
                  />
                </PaneHeader>
                <ListTitle>{t('messenger.filesLabel')}</ListTitle>
                <ListBox>
                  {filesLoading && files.length === 0 ? (
                    <SkeletonWrap>
                      {Array.from({ length: 7 }).map((_, i) => (
                        <SkeletonLine key={`file-skeleton-${i}`} width={`${66 + Math.random() * 25}%`} height="16px" />
                      ))}
                    </SkeletonWrap>
                  ) : filesError ? (
                    <ErrorText>{filesError}</ErrorText>
                  ) : !selectedChannel ? (
                    <EmptyHint>{t('messenger.selectChannel')}</EmptyHint>
                  ) : files.length === 0 ? (
                    <EmptyHint>{t('messenger.noFiles')}</EmptyHint>
                  ) : (
                    <ItemsList>
                      {files.map((file) => {
                        const key = messengerFileKey(file);
                        const isSelected = selectedFiles.has(key);
                        const sizeSelectable = isAttachmentSizeAllowed(file.size);
                        const countSelectable = isSelected || getRemainingAttachmentSlots(existingAttachmentCount, selectedFiles.size) > 0;
                        const selectable = sizeSelectable && countSelectable;
                        let disabledTitle: string | undefined;

                        if (!sizeSelectable) {
                          disabledTitle = t('email.fileTooLarge');
                        } else if (!countSelectable) {
                          disabledTitle = t('email.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT });
                        }

                        return (
                          <FileRow
                            key={key}
                            $disabled={!selectable}
                            onClick={() => handleToggleFile(file)}
                            title={disabledTitle}
                          >
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleToggleFile(file)}
                              disabled={!selectable}
                            />
                            <Paperclip size={14} />
                            {isImageMessengerFile(file) && <MessengerImageEmbed file={file} />}
                            <FileMain>
                              <FileName title={file.name}>{file.name}</FileName>
                              <FileMeta>{formatFileSize(file.size)} · {providerLabel(file.provider)}</FileMeta>
                            </FileMain>
                          </FileRow>
                        );
                      })}
                    </ItemsList>
                  )}
                  {filesLoadingMore && files.length > 0 && (
                    <LoadMoreIndicator>
                      <Loader size={14} className="spin" />
                      <span>{t('email.loading')}</span>
                    </LoadMoreIndicator>
                  )}
                  {!filesLoading && selectedChannel && hasFilesNextPage && (
                    <LoadMoreBtn
                      onClick={() => { void filesQuery.fetchNextPage(); }}
                    >
                      {t('messenger.loadMore')}
                    </LoadMoreBtn>
                  )}
                </ListBox>
              </Pane>
            </BrowserArea>
          )}
        </TabContent>
        <ActionBar>
          <SelectedCount>{selectedSummary}</SelectedCount>
          <ActionButtons>
            {(() => {
              const isRefreshing = pluginsFetching
                || (activeTab === 'microsoft_teams' && teamsQuery.isFetching)
                || (activeTab === 'discord' && guildsQuery.isFetching)
                || channelsQuery.isFetching
                || filesQuery.isFetching;
              return (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // 새로고침: plugin 연결 상태부터 channels/files 까지 전부 다시 조회
                    // (이전에는 filesQuery 만 refetch 해서 연결 상태/팀/채널 캐시가 stale 인 채로 남았음)
                    void refetchPlugins();
                    if (activeTab === 'microsoft_teams') {
                      void teamsQuery.refetch();
                    } else if (activeTab === 'discord') {
                      void guildsQuery.refetch();
                    }
                    void channelsQuery.refetch();
                    if (selectedChannel) {
                      void filesQuery.refetch();
                    }
                  }}
                  disabled={isRefreshing}
                >
                  <SpinningRefreshIcon size={14} $spinning={isRefreshing} />
                  {t('messenger.refresh')}
                </Button>
              );
            })()}
            <Button
              variant="primary"
              size="sm"
              disabled={selectedFiles.size === 0}
              onClick={handleAttach}
            >
              {t('email.attach')}
            </Button>
          </ActionButtons>
        </ActionBar>
      </ModalBody>
    </Modal>
  );
};

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinningRefreshIcon = styled(RefreshCw)<{ $spinning?: boolean }>`
  ${({ $spinning }) =>
    $spinning &&
    css`
      animation: ${spinAnimation} 0.8s linear infinite;
    `}
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const TabContent = styled.div`
  margin-top: 10px;
  flex: 1;
  min-height: 0;
`;

const LoadingWrap = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
`;

const NotConnectedArea = styled.div`
  height: 100%;
  display: grid;
  place-items: center;
`;

const TabIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
`;

const MessengerLogoWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 22%;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  display: grid;
  place-items: center;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.3)'
      : '0 4px 16px rgba(0,0,0,0.08)'};

  img {
    width: 48px;
    height: 48px;
    object-fit: contain;
    display: block;
  }
`;

const BrowserArea = styled.div`
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: auto;
  }
`;

const Pane = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: none;
  border-radius: 0;
  background: transparent;

  &:first-child {
    border-right: 0.5px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }
`;

const PaneHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const ScopeSelectWrap = styled.div`
  display: flex;
  gap: 8px;
`;

const NativeSelect = styled.select`
  min-width: 160px;
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.85rem;
  padding: 8px 10px;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)'};
    outline: none;
  }
`;

const ListTitle = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 8px 12px 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ListBox = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 6px;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ChannelRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  border-radius: 10px;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,149,0,0.15)'
        : 'rgba(255,149,0,0.12)'
      : 'transparent'};
  color: ${({ theme }) => theme.colors.textPrimary};
  padding: 10px 12px;
  font-size: 0.86rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark'
          ? 'rgba(255,149,0,0.18)'
          : 'rgba(255,149,0,0.15)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.04)'};
  }

  svg {
    transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  }
  &:hover svg {
    transform: scale(1.1) rotate(-4deg);
  }
`;

const FileRow = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  border: none;
  border-radius: 10px;
  padding: 10px 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  background: transparent;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled
        ? 'transparent'
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')};
  }
`;

const FileMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ImageEmbedWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  overflow: hidden;
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'};
  flex-shrink: 0;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${FileRow}:hover & {
    transform: scale(1.08) rotate(-4deg);
  }
`;

const ImageEmbed = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ImageEmbedSkeleton = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.bgHover};
`;

const ImageEmbedFallback = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  font-size: 0.62rem;
  color: ${({ theme }) => theme.colors.textMuted};
  letter-spacing: 0.03em;
`;

const FileName = styled.span`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileMeta = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.74rem;
`;

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 6px;
`;

const EmptyHint = styled.div`
  padding: 20px 12px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.82rem;
`;

const ErrorText = styled.div`
  padding: 12px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.82rem;
  line-height: 1.45;
`;

const LoadMoreBtn = styled.button`
  width: 100%;
  border: 0.5px dashed ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border-radius: 10px;
  padding: 8px 10px;
  margin-top: 8px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.16)'};
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }
`;

const LoadMoreIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8rem;

  .spin {
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ActionBar = styled.div`
  margin-top: 0;
  padding-top: 12px;
  border-top: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SelectedCount = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.82rem;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

